import { useEffect, useMemo } from "react";
import { useMatch } from "react-router-dom";
import { observer } from "mobx-react-lite";

import { PARTY_MARKET_SECTION_PATH } from "@/app/constants/routes";
import { useQueryParams } from "@/shared/hooks/use-query-params";
import { getMarketSectionLink } from "@/shared/routing";
import { useMarketsState } from "@/shared/stores/app-state";
import { joinMarket } from "@/shared/utils/joins";

import { BreadCrumbSelectItem } from "../bread-crumb-select-item";
import { BreadCrumbItemProps, MenuItem } from "../types";

const useGetMarketsMenuList = () => {
	const marketsStore = useMarketsState();
	const query = useQueryParams().toString();
	const route = useMatch(PARTY_MARKET_SECTION_PATH);
	const { partyUUID = "" } = route?.params || {};

	useEffect(() => {
		if (partyUUID) marketsStore.loadData(partyUUID);
	}, [partyUUID, marketsStore]);

	const menuList = useMemo((): MenuItem[] => {
		if (!partyUUID) return [];

		const newQuery = query ? `?${query}` : "";
		const marketsList = marketsStore.marketsMap[partyUUID] || [];

		return marketsList.map((market) => {
			const marketLabel = joinMarket(market);
			const link = getMarketSectionLink({
				marketSection: "algos",
				marketUUID: market.marketUUID,
				partyUUID: partyUUID || "",
			});

			return { value: market.marketUUID, label: marketLabel, redirectPath: `${link}${newQuery}` };
		});
		// Disable eslint because marketsStore.marketsMap[partyUUID] is MobX observable
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [marketsStore.marketsMap[partyUUID], partyUUID, query]);

	return menuList;
};

export const MarketBreadCrumb = observer(({ route, paths }: BreadCrumbItemProps) => {
	const menuList = useGetMarketsMenuList();

	return (
		<BreadCrumbSelectItem route={route} paths={paths} menuList={menuList} popoverWidth={200} />
	);
});
